import { createRoot } from "react-dom/client";
import { Capacitor } from "@capacitor/core";
import App from "./App";
import { RootErrorBoundary } from "./components/RootErrorBoundary";
import { handleStaleChunkError } from "./lib/staleChunkReload";
import { initBackdropViewport } from "./lib/backdropViewport";
import { captureFirstTouch, captureAndroidInstallReferrer } from "./lib/acquisition";
import { initSentry } from "./lib/sentry";
import { initAttribution } from "./lib/attribution";
import { installConsoleNoiseFilter } from "./lib/consoleNoiseFilter";
import { runBuildMigrations } from "./lib/buildMigrations";
import {
  resetLandingVariantToMain,
  foldFormDexRowsIntoMain,
  repairBaseSpeciesFormMirrors,
  repairFormParentCascade,
} from "./lib/dexStateStore";
import "./index.css";

// Build 234: run one-shot client storage migrations synchronously BEFORE any
// store or context provider initialises, so stale on-device caches (old
// sprite overrides, orphaned scan snapshots left behind by the server
// pre-launch wipe) are gone before any hydration reads them. Self-guarded and
// total - see buildMigrations.ts.
runBuildMigrations();

// Landing default: every cold app launch opens the Pokédex on the Main dex,
// regardless of the variant last viewed in a previous session, so the grid that
// preloads is the one the trainer lands on. Runs pre-React so the Pokédex page's
// first variant read already returns Main (robust even on a deep-link launch
// straight to /pokedex). In-session pill selection still tracks normally.
resetLandingVariantToMain();

// 2026-08-11: one-shot repair of stale base-species-form mirrors. The mirror
// used to be write-only, so every untick left the species' own base FORM row
// asserting a catch the trainer had taken back — which the Compare API then
// folded back onto the base row and reported as "you have". Self-guarded,
// idempotent, and it enqueues each correction for the normal debounced PUT so
// the device pushes the truth up rather than waiting on a server cleanup.
// See repairBaseSpeciesFormMirrors.
repairBaseSpeciesFormMirrors();

// 2026-09-01: one-shot fold of the Regional / Costume dexes' own rows into the
// MAIN form rows the whole app now reads. Those two dexes are a view over the
// species page's Forms and Costumes cells rather than a namespace of their own,
// so a tick made before this build has to move to the row it now shares.
// Runs BEFORE the parent-species repair so a folded catch also lights its
// species row. Self-guarded, idempotent, syncs. See foldFormDexRowsIntoMain.
foldFormDexRowsIntoMain();

// 2026-08-29: one-shot repair of form catches stranded without their species
// rows. Write paths that predated (or skipped) the parent-species cascade left
// a present FORM row with the Shiny/Main cell above it still missing, so a
// recorded event costume/regional never surfaced in the dex it belongs to.
// Self-guarded, idempotent, syncs its corrections. See repairFormParentCascade.
repairFormParentCascade();

// b18-telemetry-noise-filter: install the iOS WebKit noise filter before
// any other app code runs. This suppresses
// WebProcess::markAllLayersVolatile / didBecomeUnresponsive messages from
// the JS console path so subsequent telemetry captures are readable.
// Only active on iOS native builds. No-op on web and Android.
installConsoleNoiseFilter();

// Workstream C launch readiness: initialise Sentry before React renders so
// any provider/router crash on the first paint reaches the dashboard. The
// init is a no-op when VITE_SENTRY_DSN is unset, so non-production builds
// run unchanged.
initSentry();

// 2026-10-13: AppsFlyer install attribution for the Reddit Ads test. No-op on
// web and whenever VITE_APPSFLYER_DEV_KEY is unset. Not awaited: nothing about
// the first paint depends on it, and it swallows its own failures.
void initAttribution();

// The app is primarily hash-routed (so static hosting serves a single
// index.html for every URL), but a few routes are served as real paths so
// they can be deep-linked, bookmarked, and opened from email — notably the
// admin captures dashboard at /portal/admin/* and the email verification
// flow at /auth/*. For those paths we must NOT inject a "#/" hash, because
// AppRouter would then read the empty hash as "/" and render the home page
// instead of the requested route.
const PATH_ROUTED_PREFIXES = ["/portal/admin/", "/auth/"];
const isPathRouted = PATH_ROUTED_PREFIXES.some((p) =>
  window.location.pathname.startsWith(p),
);
if (!isPathRouted && !window.location.hash) {
  window.location.hash = "#/";
}

// Native iOS / Android bundles are App Store builds and must start every new
// user with an empty roster (no sample inventory, no preloaded box). The
// demoMode helper reads window.__NEXUS_APP_MODE__ on boot so we set it
// before React renders. Web prod build is forced to production via the
// vite-injected import.meta.env.PROD flag below.
if (Capacitor.isNativePlatform()) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).__NEXUS_APP_MODE__ = "production";
} else if (import.meta.env.PROD) {
  // Production web build (app.nexusdex.ai) must also start brand-new users
  // with an empty roster. The vite dev server still falls through to "demo"
  // mode for designer review.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).__NEXUS_APP_MODE__ = "production";
}

// v1.2.1(13): Force the pre-paint theme to DARK on all native builds and on
// every unauthenticated landing (Welcome / Login / Signup). The brand surface
// is dark, and shipping a light-mode flash before SessionProvider boots was
// the #1 polish complaint for build 12. SessionProvider still reconciles to
// the user's Auto/Light/Dark preference once it mounts; this only sets the
// initial paint.
document.documentElement.classList.add("dark");
document.documentElement.style.colorScheme = "dark";

// Platform hook for platform-specific styling. iOS reserves a large safe-area
// inset for the notch / Dynamic Island; Android has a thin opaque status bar
// (var(--nx-safe-top) === 0), so a hero top-padding tuned for the iPhone
// reads as too much space there. CSS scopes those tweaks to `platform-android`
// / `platform-ios` off this class rather than forking whole pages per platform.
// Capacitor.getPlatform() returns "ios" | "android" | "web".
document.documentElement.classList.add(`platform-${Capacitor.getPlatform()}`);

// Pin the page backdrop to the screen so the soft keyboard cannot resize its
// compositor layer. Native only: the web has no soft keyboard shrinking the
// viewport, and its window resizes legitimately. See lib/backdropViewport.ts
// for why the 2026-08-12 layer promotion fixed scrolling but not this.
if (Capacitor.isNativePlatform()) {
  try {
    initBackdropViewport();
  } catch {
    /* Backdrop stays viewport-sized: a slower paint, never a broken page. */
  }
}

// Wrap App in a top-level error boundary so any provider/router crash shows
// a visible debug screen instead of leaving the user staring at the body's
// dark gradient with no React tree mounted.
//
// v1.4.6 Build 6: the boot-layer handoff (pre-React splash + native splash)
// now lives in a top-level useEffect inside App, so it fires after React's
// first render COMMIT rather than after the browser's first paint (which a
// double-rAF here raced, dismissing the splash in ~67ms before React mounted).
// Vite fires this when a preloaded route chunk 404s, which is what a deploy
// does to an already-open tab. Catching it here recovers BEFORE React tries to
// render the route and throws — earlier and quieter than the boundary's own
// handling, which stays as the backstop for imports Vite does not preload.
// preventDefault stops Vite's own default of rethrowing.
// Capture where they came from BEFORE React mounts and the router rewrites the
// URL — the utm params live on the landing URL only, and are gone a tick later.
try {
  captureFirstTouch();
} catch {
  /* attribution must never be able to break a page load */
}

// Android only, and only when the web capture above found nothing: a Play
// install carries its campaign in the install referrer rather than the URL.
// Deliberately not awaited — the Play service is a bound connection and can
// take a moment, and nothing about the first paint depends on the answer.
void captureAndroidInstallReferrer().catch(() => {
  /* no Play Services, no plugin, or no referrer. All normal. */
});

window.addEventListener("vite:preloadError", (event) => {
  if (handleStaleChunkError((event as Event & { payload?: unknown }).payload ?? event)) {
    event.preventDefault();
  }
});

createRoot(document.getElementById("root")!).render(
  <RootErrorBoundary>
    <App />
  </RootErrorBoundary>,
);
